/**
 * Компонент отображения сообщения в чате - минималистичный
 */

import { memo, useState } from 'react';
import { RefreshCw, Pencil, Trash2, ChevronLeft, ChevronRight, X, Check, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DebugPanel } from './debug-panel';
import type { ChatMessage as ChatMessageType } from './types';

interface ChatMessageProps {
  message: ChatMessageType;
  /** Является ли это последним сообщением ассистента */
  isLastAssistantMessage?: boolean;
  /** Callback для перегенерации ответа */
  onRegenerate?: () => void;
  /** Идёт ли отправка сообщения */
  isSending?: boolean;
  /** Callback для удаления сообщения */
  onDelete?: (messageId: string) => void;
  /** Callback для редактирования сообщения (только user) */
  onEdit?: (messageId: string, newContent: string) => void;
  /** Callback для переключения ветки */
  onSwitchBranch?: (messageId: string, branchIndex: number) => void;
}

/**
 * Рендерит контент сообщения с базовым markdown
 */
function MessageContent({ content }: { content: string }) {
  const lines = content.split('\n');

  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {lines.map((line, i) => {
        // Заголовки
        if (line.startsWith('### ')) {
          return (
            <h4 key={i} className="font-medium text-sm mt-2 first:mt-0">
              {line.slice(4)}
            </h4>
          );
        }
        if (line.startsWith('## ')) {
          return (
            <h3 key={i} className="font-medium mt-2 first:mt-0">
              {line.slice(3)}
            </h3>
          );
        }
        if (line.startsWith('# ')) {
          return (
            <h2 key={i} className="font-semibold mt-2 first:mt-0">
              {line.slice(2)}
            </h2>
          );
        }

        // Списки
        if (line.startsWith('- ') || line.startsWith('* ')) {
          return (
            <li key={i} className="ml-4 list-disc">
              {formatInlineMarkdown(line.slice(2))}
            </li>
          );
        }

        // Нумерованные списки
        const numberedMatch = line.match(/^(\d+)\.\s(.+)$/);
        if (numberedMatch) {
          return (
            <li key={i} className="ml-4 list-decimal">
              {formatInlineMarkdown(numberedMatch[2])}
            </li>
          );
        }

        // Пустые строки
        if (!line.trim()) {
          return <div key={i} className="h-1" />;
        }

        // Обычный текст
        return (
          <p key={i}>
            {formatInlineMarkdown(line)}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Форматирует inline markdown (bold, italic, code, links)
 */
function formatInlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Найти первый match среди всех типов
    const matches: { type: string; match: RegExpMatchArray; index: number }[] = [];

    // Links: [text](url)
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch?.index !== undefined) {
      matches.push({ type: 'link', match: linkMatch, index: linkMatch.index });
    }

    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch?.index !== undefined) {
      matches.push({ type: 'bold', match: boldMatch, index: boldMatch.index });
    }

    // Code: `text`
    const codeMatch = remaining.match(/`(.+?)`/);
    if (codeMatch?.index !== undefined) {
      matches.push({ type: 'code', match: codeMatch, index: codeMatch.index });
    }

    // Italic: *text* (но не **text**)
    const italicMatch = remaining.match(/(?<!\*)\*([^*]+?)\*(?!\*)/);
    if (italicMatch?.index !== undefined) {
      matches.push({ type: 'italic', match: italicMatch, index: italicMatch.index });
    }

    // Если нет matches, добавляем остаток и выходим
    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    // Сортируем по позиции и берём первый
    matches.sort((a, b) => a.index - b.index);
    const first = matches[0];

    // Добавляем текст до match
    if (first.index > 0) {
      parts.push(remaining.slice(0, first.index));
    }

    // Обрабатываем match
    switch (first.type) {
      case 'link':
        parts.push(
          <a
            key={key++}
            href={first.match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            {first.match[1]}
          </a>
        );
        break;
      case 'bold':
        parts.push(
          <strong key={key++} className="font-medium">
            {first.match[1]}
          </strong>
        );
        break;
      case 'code':
        parts.push(
          <code
            key={key++}
            className="bg-muted/50 px-1 py-0.5 rounded text-xs font-mono"
          >
            {first.match[1]}
          </code>
        );
        break;
      case 'italic':
        parts.push(
          <em key={key++} className="italic">
            {first.match[1]}
          </em>
        );
        break;
    }

    remaining = remaining.slice(first.index + first.match[0].length);
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

/**
 * Компонент отображения источников веб-поиска
 */
function WebSearchSources({ sources }: { sources: Array<{ title: string; url: string }> }) {
  if (!sources || sources.length === 0) {return null;}

  return (
    <div className="mt-3 pt-2 border-t border-border/30">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1.5">
        <ExternalLink className="h-3 w-3" />
        <span>Источники ({sources.length})</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sources.map((source, i) => (
          <a
            key={i}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-md',
              'text-[10px] bg-muted/50 hover:bg-muted',
              'text-muted-foreground hover:text-foreground',
              'transition-colors max-w-[180px]'
            )}
            title={source.url}
          >
            <span className="truncate">{source.title || new URL(source.url).hostname}</span>
          </a>
        ))}
      </div>
    </div>
  );
}


export const ChatMessage = memo(function ChatMessage({
  message,
  isLastAssistantMessage,
  onRegenerate,
  isSending,
  onDelete,
  onEdit,
  onSwitchBranch,
}: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isTool = message.role === 'tool';

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content ?? '');
  const [isHovered, setIsHovered] = useState(false);

  const hasBranches = (message.siblingCount ?? 0) > 1;
  const currentBranch = (message.branchIndex ?? 0) + 1;
  const totalBranches = message.siblingCount ?? 1;

  const handleStartEdit = () => {
    setEditContent(message.content ?? '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(message.content ?? '');
  };

  const handleSubmitEdit = () => {
    if (editContent.trim() && onEdit) {
      onEdit(message.id, editContent.trim());
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitEdit();
    }
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handlePrevBranch = () => {
    if (onSwitchBranch && (message.branchIndex ?? 0) > 0) {
      onSwitchBranch(message.id, (message.branchIndex ?? 0) - 1);
    }
  };

  const handleNextBranch = () => {
    if (onSwitchBranch && (message.branchIndex ?? 0) < (message.siblingCount ?? 1) - 1) {
      onSwitchBranch(message.id, (message.branchIndex ?? 0) + 1);
    }
  };

  if (isTool) {
    return null;
  }

  return (
    <div
      className={cn(
        'px-3 py-1.5 group relative',
        isUser && 'flex justify-end'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 relative',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted'
        )}
      >
        {/* Режим редактирования для user сообщений */}
        {isEditing && isUser ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                'w-full min-h-[60px] p-2 rounded-lg text-sm resize-none',
                'bg-background/80 text-foreground border border-border',
                'focus:outline-none focus:ring-2 focus:ring-ring'
              )}
              autoFocus
            />
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={handleCancelEdit}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                  'text-xs bg-muted hover:bg-muted/80 text-foreground',
                  'transition-colors'
                )}
              >
                <X className="h-3 w-3" />
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSubmitEdit}
                disabled={!editContent.trim()}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                  'text-xs bg-primary hover:bg-primary/90 text-primary-foreground',
                  'transition-colors disabled:opacity-50'
                )}
              >
                <Check className="h-3 w-3" />
                Сохранить
              </button>
            </div>
          </div>
        ) : (
          <>
            {message.content ? (
              <MessageContent content={message.content} />
            ) : (
              <span className="text-muted-foreground/60 italic text-xs">
                ...
              </span>
            )}

            {/* Источники веб-поиска для assistant сообщений */}
            {isAssistant && message.metadata?.webSearchUsed && message.metadata?.webSearchSources && (
              <WebSearchSources sources={message.metadata.webSearchSources} />
            )}

            {/* Кнопки действий для user сообщений */}
            {isUser && isHovered && !isSending && (
              <div className="absolute -top-2 -right-2 flex gap-0.5 bg-background rounded-lg shadow-sm border p-0.5">
                {onEdit && (
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className={cn(
                      'p-1 rounded hover:bg-muted transition-colors',
                      'text-muted-foreground hover:text-foreground'
                    )}
                    title="Редактировать"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(message.id)}
                    className={cn(
                      'p-1 rounded hover:bg-destructive/10 transition-colors',
                      'text-muted-foreground hover:text-destructive'
                    )}
                    title="Удалить"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Навигация по веткам для user сообщений */}
        {isUser && hasBranches && !isEditing && (
          <div className="flex items-center justify-center gap-1 mt-1.5 pt-1.5 border-t border-primary-foreground/20">
            <button
              type="button"
              onClick={handlePrevBranch}
              disabled={(message.branchIndex ?? 0) === 0 || isSending}
              className={cn(
                'p-0.5 rounded hover:bg-primary-foreground/20 transition-colors',
                'disabled:opacity-30 disabled:cursor-not-allowed'
              )}
              title="Предыдущая ветка"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="text-[10px] font-medium">
              {currentBranch}/{totalBranches}
            </span>
            <button
              type="button"
              onClick={handleNextBranch}
              disabled={(message.branchIndex ?? 0) >= totalBranches - 1 || isSending}
              className={cn(
                'p-0.5 rounded hover:bg-primary-foreground/20 transition-colors',
                'disabled:opacity-30 disabled:cursor-not-allowed'
              )}
              title="Следующая ветка"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Debug Panel и кнопки действий для assistant сообщений */}
        {isAssistant && (
          <div className="mt-2 pt-1.5 border-t border-border/30">
            {/* Навигация по веткам для assistant */}
            {hasBranches && (
              <div className="flex items-center justify-center gap-1 mb-1.5 pb-1.5 border-b border-border/30">
                <button
                  type="button"
                  onClick={handlePrevBranch}
                  disabled={(message.branchIndex ?? 0) === 0 || isSending}
                  className={cn(
                    'p-0.5 rounded hover:bg-muted/80 transition-colors',
                    'text-muted-foreground hover:text-foreground',
                    'disabled:opacity-30 disabled:cursor-not-allowed'
                  )}
                  title="Предыдущая ветка"
                >
                  <ChevronLeft className="h-3 w-3" />
                </button>
                <span className="text-[10px] text-muted-foreground font-medium">
                  {currentBranch}/{totalBranches}
                </span>
                <button
                  type="button"
                  onClick={handleNextBranch}
                  disabled={(message.branchIndex ?? 0) >= totalBranches - 1 || isSending}
                  className={cn(
                    'p-0.5 rounded hover:bg-muted/80 transition-colors',
                    'text-muted-foreground hover:text-foreground',
                    'disabled:opacity-30 disabled:cursor-not-allowed'
                  )}
                  title="Следующая ветка"
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              {message.metadata ? (
                <DebugPanel
                  metadata={message.metadata}
                  actionSlot={
                    <div className="flex items-center gap-1">
                      {isLastAssistantMessage && onRegenerate && (
                        <button
                          type="button"
                          onClick={onRegenerate}
                          disabled={isSending}
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                            'text-[10px] text-muted-foreground hover:text-foreground',
                            'hover:bg-muted/50 transition-colors',
                            'disabled:opacity-50 disabled:cursor-not-allowed'
                          )}
                          title="Перегенерировать ответ"
                        >
                          <RefreshCw className={cn('h-3 w-3', isSending && 'animate-spin')} />
                          <span>Повторить</span>
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(message.id)}
                          disabled={isSending}
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                            'text-[10px] text-muted-foreground hover:text-destructive',
                            'hover:bg-destructive/10 transition-colors',
                            'disabled:opacity-50 disabled:cursor-not-allowed'
                          )}
                          title="Удалить ответ"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  }
                />
              ) : (
                <div className="flex items-center gap-1">
                  {isLastAssistantMessage && onRegenerate && (
                    <button
                      type="button"
                      onClick={onRegenerate}
                      disabled={isSending}
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                        'text-[10px] text-muted-foreground hover:text-foreground',
                        'hover:bg-muted/50 transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                      title="Перегенерировать ответ"
                    >
                      <RefreshCw className={cn('h-3 w-3', isSending && 'animate-spin')} />
                      <span>Повторить</span>
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete(message.id)}
                      disabled={isSending}
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-md',
                        'text-[10px] text-muted-foreground hover:text-destructive',
                        'hover:bg-destructive/10 transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                      title="Удалить ответ"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
